import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsAPI, type Contact } from '../../services/api';
import { Button } from '../ui';
import { Modal } from '../ui/Modal';

// Baseball stages with colors and icons
const STAGES = [
  { id: 'on_deck', label: 'On Deck', description: 'New leads, warming up', color: 'bg-blue-500', icon: '⚾' },
  { id: 'at_bat', label: 'At Bat', description: 'Showing properties', color: 'bg-yellow-500', icon: '⚾' },
  { id: 'on_base', label: 'On Base', description: 'In conversations', color: 'bg-orange-500', icon: '🏃' },
  { id: 'scoring_position', label: 'Scoring', description: 'Under contract', color: 'bg-green-500', icon: '🎯' },
  { id: 'hall_of_fame', label: 'Hall of Fame', description: 'Past clients', color: 'bg-purple-500', icon: '🏆' },
  { id: 'bench', label: 'Bench', description: 'Cold/inactive', color: 'bg-gray-500', icon: '💤' },
];

interface SortingGameProps {
  open: boolean;
  onClose: () => void;
  contacts?: Contact[];
  onComplete?: (sorted: number, skipped: number) => void;
}

interface SortedContact {
  id: string;
  stage: string;
}

export function SortingGame({ open, onClose, contacts: initialContacts, onComplete }: SortingGameProps) {
  const queryClient = useQueryClient();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sorted, setSorted] = useState<SortedContact[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [animating, setAnimating] = useState<string | null>(null);

  // Load unsorted contacts
  useEffect(() => {
    if (open) {
      if (initialContacts && initialContacts.length > 0) {
        // Use provided contacts (e.g., from import)
        setContacts(initialContacts.slice(0, 50)); // Max 50 per session
      } else {
        // Fetch unsorted contacts (those still in default stage)
        fetchUnsortedContacts();
      }
      setCurrentIndex(0);
      setSorted([]);
      setSkipped([]);
      setIsComplete(false);
    }
  }, [open, initialContacts]);

  const fetchUnsortedContacts = async () => {
    try {
      const result = await contactsAPI.list({ stage: 'on_deck', limit: 50 });
      if (result.success) {
        setContacts(result.data.contacts || []);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  // Batch update mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: SortedContact[]) => {
      // Update each contact's stage
      const promises = updates.map(({ id, stage }) =>
        contactsAPI.update(id, { stage })
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });
    },
  });

  const currentContact = contacts[currentIndex];
  const progress = contacts.length > 0 ? ((currentIndex) / contacts.length) * 100 : 0;
  const remaining = contacts.length - currentIndex;

  // Handle stage selection
  const handleSelectStage = useCallback((stageId: string) => {
    if (!currentContact || animating) return;

    setAnimating(stageId);

    // Add to sorted list
    setSorted(prev => [...prev, { id: currentContact.id, stage: stageId }]);

    // Animate and move to next
    setTimeout(() => {
      setAnimating(null);
      if (currentIndex >= contacts.length - 1) {
        setIsComplete(true);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    }, 300);
  }, [currentContact, currentIndex, contacts.length, animating]);

  // Handle skip
  const handleSkip = useCallback(() => {
    if (!currentContact || animating) return;

    setSkipped(prev => [...prev, currentContact.id]);

    if (currentIndex >= contacts.length - 1) {
      setIsComplete(true);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentContact, currentIndex, contacts.length, animating]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open || isComplete || !currentContact) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Number keys 1-6 for stages
      if (e.key >= '1' && e.key <= '6') {
        const stageIndex = parseInt(e.key) - 1;
        if (STAGES[stageIndex]) {
          handleSelectStage(STAGES[stageIndex].id);
        }
      }
      // S or Space to skip
      if (e.key === 's' || e.key === ' ') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, isComplete, currentContact, handleSelectStage, handleSkip]);

  // Save and close
  const handleFinish = async () => {
    if (sorted.length > 0) {
      await updateMutation.mutateAsync(sorted);
    }
    onComplete?.(sorted.length, skipped.length);
    onClose();
  };

  // Get initials for avatar
  const getInitials = (contact: Contact) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} size="2xl">
      <div className="px-6 pb-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Sort Your Contacts
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Quickly organize contacts into stages. Use keys 1-6 or click a stage.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
            <span>{sorted.length} sorted</span>
            <span>{remaining} remaining</span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-ondeck-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* No contacts */}
        {contacts.length === 0 && !isComplete && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🎉</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              All caught up!
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              No contacts need sorting right now.
            </p>
            <Button onClick={onClose} className="mt-4">
              Close
            </Button>
          </div>
        )}

        {/* Current contact card */}
        {currentContact && !isComplete && (
          <div className={`transition-all duration-300 ${animating ? 'scale-95 opacity-50' : ''}`}>
            {/* Contact Card */}
            <div className="bg-white dark:bg-gray-700 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-600 p-6 mb-6 text-center">
              {/* Avatar */}
              <div className="w-20 h-20 rounded-full bg-ondeck-100 dark:bg-ondeck-900 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-ondeck-600 dark:text-ondeck-400">
                  {getInitials(currentContact)}
                </span>
              </div>

              {/* Name */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                {currentContact.first_name} {currentContact.last_name}
              </h3>

              {/* Contact info */}
              <div className="text-gray-500 dark:text-gray-400 text-sm space-y-1">
                {currentContact.email && <p>{currentContact.email}</p>}
                {currentContact.phone && <p>{currentContact.phone}</p>}
                {currentContact.company && <p className="text-gray-400 dark:text-gray-500">{currentContact.company}</p>}
              </div>

              {/* Last contact */}
              {currentContact.last_contact_at && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Last contact: {new Date(currentContact.last_contact_at * 1000).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Stage buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              {STAGES.map((stage, index) => (
                <button
                  key={stage.id}
                  onClick={() => handleSelectStage(stage.id)}
                  className={`
                    relative p-4 rounded-xl border-2 transition-all duration-200
                    ${animating === stage.id
                      ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30 scale-105'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  {/* Keyboard hint */}
                  <span className="absolute top-2 left-2 w-5 h-5 rounded bg-gray-100 dark:bg-gray-600 text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center justify-center">
                    {index + 1}
                  </span>

                  <span className="text-2xl block mb-1">{stage.icon}</span>
                  <span className="font-medium text-gray-900 dark:text-white text-sm">{stage.label}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{stage.description}</span>
                </button>
              ))}
            </div>

            {/* Skip button */}
            <div className="text-center">
              <button
                onClick={handleSkip}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm underline"
              >
                Skip for now (S)
              </button>
            </div>
          </div>
        )}

        {/* Completion */}
        {isComplete && (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🎉</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Session Complete!
            </h3>
            <div className="flex justify-center gap-6 text-lg mb-6">
              <div className="text-green-600 dark:text-green-400">
                <span className="font-bold">{sorted.length}</span> sorted
              </div>
              {skipped.length > 0 && (
                <div className="text-gray-500 dark:text-gray-400">
                  <span className="font-bold">{skipped.length}</span> skipped
                </div>
              )}
            </div>

            <Button
              onClick={handleFinish}
              loading={updateMutation.isPending}
              size="lg"
            >
              Save & Finish
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
